import axios from 'axios';
import moment from 'moment';
import type { Moment } from 'moment';
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { jwtDecode } from 'jwt-decode';
import { datadogRum } from '@datadog/browser-rum';
import type { UserInterface } from '@/types/UserInterface';

const baseUrl = import.meta.env.VITE_API_AUTH_URL ?? 'http://localhost:4040';
const API = axios.create({ baseURL: `${baseUrl}/api/v1` });
const [currentLang] = navigator.language.split('-');
API.defaults.headers.common['Accept-Language'] = currentLang || 'es';

export const useAuthStore = defineStore('user', () => {
  const token = ref<string | null>(null);
  const refresh = ref<string | null>(null);
  const expires = ref<Moment | null>(null);
  const role = ref<string | null>(null);
  const id = ref<string | null>(null);
  const name = ref<string | null>(null);
  const email = ref<string | null>(null);
  const url = ref<string | null>(null);
  const version = ref<number | null>(null);
  const company = ref<string | null>(null);
  const companyId = ref<string | null>(null);
  const isCreatingAccount = ref(false);
  const stepCreatingAccount = ref(1);
  const planSelected = ref<string | null>(null);
  const countrySelected = ref<string | null>(null);
  const forceChangePassword = ref(false);
  const requireFinishAccount = ref<string | null>(null);
  const isLoading = ref(false);

  const isValidToken = computed(() => {
    const date = moment(expires.value);

    return !!role.value && !!id.value && date.isValid() && expires.value && date >= moment();
  });

  const verify = (_token?: string | null, _refresh?: string | null) => {
    token.value = _token || null;
    refresh.value = _refresh || null;

    return jwtDecode<UserInterface>(token.value!);
  };

  const setData = (payload: {
    token: string
    url: string
    version: number
    forceChangePassword: boolean
    refresh: string
  }) => {
    url.value = payload.url;
    token.value = payload.token;
    version.value = payload.version ?? 1;
    refresh.value = payload.refresh;
    forceChangePassword.value = payload.forceChangePassword;
    decodeUser();
  };

  const resetData = () => {
    id.value = null;
    url.value = null;
    role.value = null;
    name.value = null;
    token.value = null;
    refresh.value = null;
    expires.value = null;
    version.value = null;
    company.value = null;
    companyId.value = null;
  };

  const decodeUser = () => {
    try {
      const user = jwtDecode<UserInterface>(token.value!);
      expires.value = user.exp ? moment.unix(user.exp) : moment();
      role.value = user.role;
      id.value = user.id;
      name.value = user.name;
      email.value = user.email;
      companyId.value = user.company || null;
    } catch (e) {
      datadogRum.addError(e);
      resetData();
    }
  };

  const refreshToken = async (refreshToken: string) => {
    const { data } = await API.post('/refreshToken', { refreshToken });
    token.value = data.token;
    decodeUser();
  };

  const getBaseUrl = (): string => {
    if (url.value) {
      return url.value
        .replace('{{TOKEN}}', token.value || '')
        .replace('{{COMPANY}}', companyId.value || '');
    }

    if (role.value === 'agent') {
      return `${import.meta.env.VITE_CHAT_BASE_URL}?data=${token.value}`;
    }

    return `${import.meta.env.VITE_ADMIN_BASE_URL}?data=${token.value}`;
  };

  const fetchUser = async () => {
    const { data } = await API.get('/users/auth', {
      headers: {
        Authorization: `Bearer ${token.value}`,
      },
    });
    companyId.value = data.company;
  };

  const logout = async () => {
    try {
      await API.post('/logout', {}, {
        headers: {
          Authorization: `Bearer ${token.value}`,
        },
      });
    } catch (e) {
      datadogRum.addError(e);
    } finally {
      resetData();
    }
  };

  return {
    token,
    refresh,
    expires,
    role,
    id,
    name,
    email,
    url,
    version,
    company,
    companyId,
    isCreatingAccount,
    stepCreatingAccount,
    planSelected,
    countrySelected,
    forceChangePassword,
    requireFinishAccount,
    isLoading,
    isValidToken,
    verify,
    setData,
    resetData,
    decodeUser,
    refreshToken,
    getBaseUrl,
    fetchUser,
    logout,
  };
}, {
  persistedState: {
    persist: true,
  },
});
